import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Server, Zap, Calendar, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

const EnterpriseFunnel = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    infrastructure: '',
    brokers: '',
    timeline: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSelection = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (step < 4) {
      setTimeout(() => setStep(step + 1), 300); // Small delay for UX
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Honeypot check
    if (e.target.botcheck && e.target.botcheck.checked) {
      setIsSubmitting(false);
      return; // Silently drop bot submissions
    }

    const payload = {
      access_key: '9aaf24c6-51c0-46c0-a1d7-b1f26fc673d9',
      subject: 'New Institutional Architecture Lead - VegaVelox',
      from_name: 'VegaVelox B2B Portal',
      email: formData.email,
      Infrastructure_Need: formData.infrastructure,
      Target_Brokers: formData.brokers,
      Deployment_Timeline: formData.timeline
    };

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError(result.message || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const OptionCard = ({ icon: Icon, title, description, field, value }) => {
    const isSelected = formData[field] === value;
    return (
      <div 
        onClick={() => handleSelection(field, value)}
        className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center text-center ${
          isSelected 
          ? 'border-[hsl(var(--primary-gold))] bg-[hsl(var(--primary-gold))]/10' 
          : 'border-[hsl(var(--border))] bg-[hsl(var(--aztec-dark))] hover:border-[hsl(var(--primary-gold))]/50 hover:bg-[hsl(var(--primary-gold))]/5'
        }`}
      >
        <Icon className={`w-10 h-10 mb-4 ${isSelected ? 'text-[hsl(var(--primary-gold))]' : 'text-gray-400'}`} />
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    );
  };

  return (
    <section id="enterprise-audit" className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 gradient-text">Request Architecture Audit</h2>
          <p className="text-xl text-gray-300">Tell us about your infrastructure bottlenecks to qualify for a bespoke consulting session.</p>
        </div>

        <div className="glass-effect rounded-2xl p-8 md:p-12 min-h-[450px] relative">
          
          {/* Back Button */}
          {step > 1 && !isSubmitted && (
            <button 
              onClick={handleBack}
              className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center text-gray-400 hover:text-white transition-colors z-20"
            >
              <ArrowLeft className="w-5 h-5 mr-1" /> Back
            </button>
          )}

          <AnimatePresence mode="wait">
            {step === 1 && !isSubmitted && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="text-2xl font-semibold text-white mb-8 text-center">1. What infrastructure do you need built?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <OptionCard icon={Zap} title="Broker Bridging" description="Low-latency API integration and smart routing." field="infrastructure" value="Broker Bridging" />
                  <OptionCard icon={Server} title="Data Center Arch" description="Colocation, cloud migration, and network design." field="infrastructure" value="Data Center Arch" />
                  <OptionCard icon={CheckCircle2} title="Quant Analytics" description="AI Swarm development and strategy backtesting." field="infrastructure" value="Quant Analytics" />
                </div>
              </motion.div>
            )}

            {step === 2 && !isSubmitted && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="text-2xl font-semibold text-white mb-8 text-center">2. Target Brokers or Environment?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <OptionCard icon={Server} title="Dhan / Kite" description="Indian retail & prop APIs." field="brokers" value="Dhan/Kite" />
                  <OptionCard icon={CheckCircle2} title="Institutional" description="Interactive Brokers, FIX APIs." field="brokers" value="FIX/Institutional" />
                  <OptionCard icon={Zap} title="Custom/Internal" description="Proprietary dark pools or internal matching." field="brokers" value="Custom" />
                </div>
              </motion.div>
            )}

            {step === 3 && !isSubmitted && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h3 className="text-2xl font-semibold text-white mb-8 text-center">3. Deployment Timeline?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <OptionCard icon={Zap} title="ASAP" description="Immediate priority." field="timeline" value="ASAP" />
                  <OptionCard icon={Calendar} title="1-3 Months" description="Q1/Q2 roadmap." field="timeline" value="1-3 Months" />
                  <OptionCard icon={CheckCircle2} title="Planning" description="Gathering architecture specs." field="timeline" value="Planning Phase" />
                </div>
              </motion.div>
            )}

            {step === 4 && !isSubmitted && (
              <motion.div key="step4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md mx-auto text-center">
                <h3 className="text-3xl font-bold text-white mb-4">Final Step</h3>
                <p className="text-gray-300 mb-8">Enter your work email to receive the architecture proposal and booking link.</p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  {/* Honeypot field for /vegaosi Anti-Spam protection */}
                  <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                  
                  <input 
                    type="email" 
                    required 
                    placeholder="name@fund.com" 
                    className="w-full bg-gray-800/50 border-2 border-[hsl(var(--border))] rounded-lg px-6 py-4 text-white focus:outline-none focus:border-[hsl(var(--primary-gold))] transition-colors"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                  
                  {error && (
                    <p className="text-red-400 text-sm text-left">{error}</p>
                  )}

                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`w-full bg-gradient-to-r from-[hsl(var(--peacock-blue))] to-[hsl(var(--light-gold))] text-white font-bold py-4 px-8 rounded-lg hover:opacity-90 transition-opacity flex justify-center items-center gap-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Audit Request'} <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              </motion.div>
            )}

            {isSubmitted && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Request Received</h3>
                <p className="text-gray-300">Your architecture requirements have been logged. I will review the spec and contact you shortly.</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Progress Bar */}
          {!isSubmitted && (
             <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-800 rounded-b-2xl overflow-hidden">
               <motion.div 
                 className="h-full bg-gradient-to-r from-[hsl(var(--peacock-blue))] to-[hsl(var(--primary-gold))]"
                 initial={{ width: '25%' }}
                 animate={{ width: `${(step / 4) * 100}%` }}
                 transition={{ duration: 0.5 }}
               />
             </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default EnterpriseFunnel;
